import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const formatElapsed = (dateStr) => {
	if (!dateStr) return "";
	const diffMs = Date.now() - new Date(dateStr).getTime();
	const minutes = Math.floor(diffMs / (1000 * 60));
	if (minutes < 60) return `${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h`;
	const days = Math.floor(hours / 24);
	return `${days}d ${hours % 24}h`;
};

const BoardMoveTicketModal = ({ open, onClose, ticketId }) => {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [currentCards, setCurrentCards] = useState([]);
	const [boards, setBoards] = useState([]);
	const [phases, setPhases] = useState([]);
	const [selectedBoardId, setSelectedBoardId] = useState("");
	const [selectedPhaseId, setSelectedPhaseId] = useState("");

	useEffect(() => {
		if (!open) return;
		const fetchInitial = async () => {
			setLoading(true);
			try {
				const [{ data: cards }, { data: boardsList }] = await Promise.all([
					api.get(`/tickets/${ticketId}/cards`),
					api.get("/boards"),
				]);
				setCurrentCards(cards || []);
				setBoards(boardsList || []);
				const defaultBoardId = cards?.[0]?.boardId || boardsList?.[0]?.id || "";
				setSelectedBoardId(defaultBoardId);
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};
		fetchInitial();
	}, [open, ticketId]);

	useEffect(() => {
		if (!selectedBoardId) {
			setPhases([]);
			return;
		}
		const fetchPhases = async () => {
			try {
				const { data } = await api.get(`/boards/${selectedBoardId}`);
				setPhases(data.phases || []);
				const existingCard = currentCards.find(
					(c) => String(c.boardId) === String(selectedBoardId)
				);
				setSelectedPhaseId(
					existingCard ? existingCard.phaseId : data.phases?.[0]?.id || ""
				);
			} catch (err) {
				toastError(err);
			}
		};
		fetchPhases();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedBoardId]);

	const handleClose = () => {
		setSelectedBoardId("");
		setSelectedPhaseId("");
		setCurrentCards([]);
		onClose();
	};

	const handleConfirm = async () => {
		if (!selectedBoardId || !selectedPhaseId) return;
		setSaving(true);
		try {
			await api.put(`/tickets/${ticketId}/move-card`, {
				boardId: selectedBoardId,
				targetPhaseId: selectedPhaseId,
			});
			toast.success("Kanban atualizado!");
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const activeCard = currentCards.find(
		(c) => String(c.boardId) === String(selectedBoardId)
	);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
			<DialogTitle>Mover no Quadro</DialogTitle>
			<DialogContent dividers>
				{loading ? (
					<CircularProgress size={24} />
				) : boards.length === 0 ? (
					<Typography variant="body2" color="textSecondary">
						Nenhum quadro cadastrado ainda.
					</Typography>
				) : (
					<>
						<TextField
							select
							label="Quadro"
							fullWidth
							margin="dense"
							variant="outlined"
							value={selectedBoardId}
							onChange={(e) => setSelectedBoardId(e.target.value)}
						>
							{boards.map((b) => (
								<MenuItem key={b.id} value={b.id}>
									{b.name}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							label="Fase"
							fullWidth
							margin="dense"
							variant="outlined"
							value={selectedPhaseId}
							onChange={(e) => setSelectedPhaseId(e.target.value)}
							disabled={phases.length === 0}
						>
							{phases.map((p) => (
								<MenuItem key={p.id} value={p.id}>
									{p.name}
								</MenuItem>
							))}
						</TextField>
						{activeCard && activeCard.enteredPhaseAt && (
							<Typography variant="caption" color="textSecondary">
								Nessa fase há {formatElapsed(activeCard.enteredPhaseAt)}
							</Typography>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="secondary">
					Cancelar
				</Button>
				<Button
					onClick={handleConfirm}
					color="primary"
					variant="contained"
					disabled={saving || !selectedBoardId || !selectedPhaseId}
				>
					{activeCard ? "Mover" : "Adicionar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default BoardMoveTicketModal;
